import { clsx } from 'clsx';
import DangerIconButton from '@/components/buttons/DangerIconButton.tsx';
import { Plus, Trash } from 'lucide-react';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import React from 'react';
import { UseFieldArrayReturn, useFormContext, get } from 'react-hook-form';
import { TourOptionFormData } from '@/features/dashboard/tourOptions/schemas/tourOptionSchema.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import PrimitiveCheckbox from '@/components/form/_primitives/PrimitiveCheckbox.tsx';
import { cn } from '@/lib/utils.ts';

const AgeRestrictionPriceForm = ({
  currency,
  fieldArray,
  parentFieldName,
  limitsEnabled,
  usedPriceIds,
}: {
  currency?: string;
  fieldArray: UseFieldArrayReturn<TourOptionFormData>;
  parentFieldName: '' | `variants.${number}`;
  limitsEnabled: boolean;
  usedPriceIds?: Set<string>;
}) => {
  const {
    formState: { errors },
    watch,
  } = useFormContext<TourOptionFormData>();

  const { fields, append, remove } = fieldArray;

  let definedAgeRangesField:
    | 'isVariantOnlyForDefinedAgeRanges'
    | `variants.${number}.isVariantOnlyForDefinedAgeRanges`;
  if (parentFieldName === '')
    definedAgeRangesField = 'isVariantOnlyForDefinedAgeRanges';
  else
    definedAgeRangesField = `${parentFieldName}.isVariantOnlyForDefinedAgeRanges`;
  const isVariantOnlyForDefinedAgeRangesValue = watch(definedAgeRangesField);

  return (
    <>
      <div
        className={cn(
          'mb-1 grid gap-2 bg-neutral-50 px-4 pb-1 pt-2',
          limitsEnabled
            ? 'grid-cols-[1fr_1fr_1fr_1fr_44px_34px]'
            : 'grid-cols-[1fr_1fr_1fr_44px_34px]',
        )}
      >
        <InputLabel label={'Wiek minimalny'} required />
        <InputLabel label={'Wiek maksymalny'} required />
        <InputLabel label={'Koszt'} required />
        {limitsEnabled && <InputLabel label={'Limit'} required />}
        <InputLabel label={'Arch.'} />
      </div>
      <div
        className={clsx(
          'grid gap-x-2 gap-y-1 px-4 pb-2',
          limitsEnabled
            ? 'grid-cols-[1fr_1fr_1fr_1fr_44px_34px]'
            : 'grid-cols-[1fr_1fr_1fr_44px_34px]',
        )}
      >
        {fields.map((field, index) => (
          <AgeRestrictionPriceRow
            key={field.id}
            index={index}
            remove={remove}
            currency={currency}
            parentFieldName={parentFieldName}
            limitsEnabled={limitsEnabled}
            usedPriceIds={usedPriceIds}
          />
        ))}
        {!isVariantOnlyForDefinedAgeRangesValue && (
          <BasePriceRow
            currency={currency}
            parentFieldName={parentFieldName}
            limitsEnabled={limitsEnabled}
          />
        )}
      </div>
      <div className="flex flex-col items-end justify-end gap-2 border-t border-neutral-200 bg-basic-white px-4 py-2">
        <InputError
          error={get(
            errors,
            parentFieldName === ''
              ? 'ageRestrictionPrices.root.message'
              : `${parentFieldName}.ageRestrictionPrices.root.message`,
          )}
        />
        <ActionButton
          tone="secondary"
          size="xs"
          onClick={() => append({ price: 0 })}
        >
          <Plus />
          Dodaj grupę wiekową
        </ActionButton>
      </div>
    </>
  );
};

const AgeRestrictionPriceRow = ({
  index,
  remove,
  currency,
  parentFieldName,
  limitsEnabled,
  usedPriceIds,
}: {
  index: number;
  remove: (index) => void;
  currency?: string;
  parentFieldName: '' | `variants.${number}`;
  limitsEnabled: boolean;
  usedPriceIds?: Set<string>;
}) => {
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext<TourOptionFormData>();

  let fieldPrefix:
    | 'ageRestrictionPrices'
    | `variants.${number}.ageRestrictionPrices`;
  if (parentFieldName === '') fieldPrefix = 'ageRestrictionPrices';
  else fieldPrefix = `${parentFieldName}.ageRestrictionPrices`;

  const priceId = watch(`${fieldPrefix}.${index}.id`);
  const isPriceUsed = !!priceId && usedPriceIds?.has(priceId) === true;

  return (
    <>
      <ValueInput
        register={register(`${fieldPrefix}.${index}.minAge`)}
        placeholder="0"
        type="number"
        sufix="lat"
        error={get(errors, `${fieldPrefix}.${index}.minAge.message`)}
      />
      <ValueInput
        register={register(`${fieldPrefix}.${index}.maxAge`)}
        placeholder="0"
        type="number"
        sufix="lat"
        error={get(errors, `${fieldPrefix}.${index}.maxAge.message`)}
      />
      <ValueInput
        register={register(`${fieldPrefix}.${index}.price`, {
          valueAsNumber: true,
        })}
        placeholder="0"
        type="number"
        step={0.01}
        sufix={currency}
        error={get(errors, `${fieldPrefix}.${index}.price.message`)}
      />
      {/*{limitsEnabled && (*/}
      {/*  <ValueInput*/}
      {/*    register={register(`${fieldPrefix}.${index}.participantLimit`)}*/}
      {/*    placeholder="0"*/}
      {/*    type="number"*/}
      {/*    sufix="osób"*/}
      {/*    error={get(*/}
      {/*      errors,*/}
      {/*      `${fieldPrefix}.${index}.participantLimit.message`,*/}
      {/*    )}*/}
      {/*  />*/}
      {/*)}*/}
      <div className="flex items-center justify-center">
        {priceId && (
          <PrimitiveCheckbox
            title="Wycofana z oferty (archiwum)"
            {...register(`${fieldPrefix}.${index}.archived`)}
          />
        )}
      </div>
      <DangerIconButton
        onClick={() => remove(index)}
        disabled={isPriceUsed}
        title={
          isPriceUsed
            ? 'Nie można usunąć — cena jest używana w rezerwacjach. Zamiast tego oznacz ją jako wycofaną z oferty.'
            : 'Usuń cenę'
        }
      >
        <Trash />
      </DangerIconButton>
    </>
  );
};

const BasePriceRow = ({
  currency,
  parentFieldName,
  limitsEnabled,
}: {
  currency?: string;
  parentFieldName: '' | `variants.${number}`;
  limitsEnabled: boolean;
}) => {
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext<TourOptionFormData>();
  let priceFieldName: 'basePrice' | `variants.${number}.price`;
  if (parentFieldName === '') priceFieldName = 'basePrice';
  else priceFieldName = `${parentFieldName}.price`;

  let participantLimitFieldName:
    | 'participantLimit'
    | `variants.${number}.participantLimit`;
  if (parentFieldName === '') participantLimitFieldName = 'participantLimit';
  else participantLimitFieldName = `${parentFieldName}.participantLimit`;

  let basePriceIdFieldName: 'basePriceId' | `variants.${number}.basePriceId`;
  let basePriceArchivedFieldName:
    | 'basePriceArchived'
    | `variants.${number}.basePriceArchived`;
  if (parentFieldName === '') {
    basePriceIdFieldName = 'basePriceId';
    basePriceArchivedFieldName = 'basePriceArchived';
  } else {
    basePriceIdFieldName = `${parentFieldName}.basePriceId`;
    basePriceArchivedFieldName = `${parentFieldName}.basePriceArchived`;
  }
  const basePriceId = watch(basePriceIdFieldName);

  return (
    <>
      <div className="col-span-2 flex items-center text-sm text-base-text">
        Pozostali
      </div>
      <ValueInput
        register={register(priceFieldName, { valueAsNumber: true })}
        placeholder="0"
        sufix={currency}
        error={get(errors, `${priceFieldName}.message`)}
      />
      {limitsEnabled && (
        <ValueInput
          register={register(participantLimitFieldName, {
            valueAsNumber: true,
          })}
          placeholder="0"
          type="number"
          sufix="osób"
          error={get(errors, `${priceFieldName}.message`)}
        />
      )}
      <div className="flex items-center justify-center">
        {basePriceId && (
          <PrimitiveCheckbox
            title="Wycofana z oferty (archiwum)"
            {...register(basePriceArchivedFieldName)}
          />
        )}
      </div>
    </>
  );
};

export default AgeRestrictionPriceForm;
